/**
 * The urgent / important matrix. Stored on a work item's flexible attributes as two booleans, so it needs no
 * schema change: `urgent` and `important`. A missing key means nobody has answered yet, and that is what the
 * app asks about — it is never guessed.
 */
export const URGENT_KEY = 'urgent'
export const IMPORTANT_KEY = 'important'

export type QuadrantKey = 'do_now' | 'schedule' | 'delegate' | 'eliminate'

export interface Quadrant {
  key: QuadrantKey
  title: string
  short: string
  urgent: boolean
  important: boolean
  examples: string[]
  action: string
}

export const QUADRANTS: Quadrant[] = [
  {
    key: 'do_now',
    title: 'Do now',
    short: 'Urgent · Important',
    urgent: true,
    important: true,
    examples: ['Customers cannot log in after a release', 'A contract expires tonight'],
    action: 'Drop other things, fix it, and keep people posted.',
  },
  {
    key: 'schedule',
    title: 'Schedule',
    short: 'Important · Not urgent',
    urgent: false,
    important: true,
    examples: ['Redesign the onboarding flow', 'Write the hiring scorecard'],
    action: 'Put time on the calendar and make steady progress.',
  },
  {
    key: 'delegate',
    title: 'Delegate',
    short: 'Urgent · Not important',
    urgent: true,
    important: false,
    examples: ['Book a vendor call', 'Collect numbers somebody else already has'],
    action: 'Hand it to the right person with a date.',
  },
  {
    key: 'eliminate',
    title: 'Eliminate',
    short: 'Not urgent · Not important',
    urgent: false,
    important: false,
    examples: ['Re-formatting an old slide deck', 'A weekly status meeting nobody uses'],
    action: 'Stop doing it, or make it smaller.',
  },
]

type Attrs = Record<string, unknown> | null | undefined

/** The quadrant an item sits in, or null when either question is still unanswered. */
export function quadrantOf(attrs: Attrs): Quadrant | null {
  const urgent = attrs?.[URGENT_KEY]
  const important = attrs?.[IMPORTANT_KEY]
  if (typeof urgent !== 'boolean' || typeof important !== 'boolean') return null
  return QUADRANTS.find((q) => q.urgent === urgent && q.important === important) ?? null
}

/** The whole attribute map with the answer set (or cleared with null), so a PATCH never drops other attributes. */
export function withMatrix(attrs: Attrs, answer: { urgent: boolean; important: boolean } | null): Record<string, unknown> {
  const next = { ...(attrs ?? {}) }
  if (answer) {
    next[URGENT_KEY] = answer.urgent
    next[IMPORTANT_KEY] = answer.important
  } else {
    delete next[URGENT_KEY]
    delete next[IMPORTANT_KEY]
  }
  return next
}
